import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { projectRisksApi } from '../../../api/projectWorkspace';
import { ApiError } from '../../../api/client';
import { StatusBadge } from '../../../components/StatusBadge';
import { SectionHeader } from '../../../components/SectionHeader';
import { DataTable } from '../../../components/DataTable';
import { Modal } from '../../../components/Modal';
import { useToast } from '../../../components/Toast';
import { usePermission } from '../../../auth/AuthContext';
import { Project } from '../../../types/projects';
import { CommentThread } from '../../../components/CommentThread';

const riskSchema = z.object({
  riskDescription: z.string().min(5),
  category: z.enum(['security', 'access', 'programme', 'financial', 'operational', 'procurement', 'staffing', 'reputational', 'safeguarding', 'environmental', 'political', 'other']),
  likelihood: z.enum(['low', 'medium', 'high']),
  impact: z.enum(['low', 'medium', 'high']),
  mitigation: z.string().optional(),
});

const RATING_COLORS: Record<string, string> = { low: '#15803d', medium: '#b45309', high: '#dc2626', critical: '#991b1b' };

export function ProjectRiskTab({ project }: { project: Project }) {
  const canManage = usePermission('project_risks.manage');
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);

  const risksQuery = useQuery({ queryKey: ['project-risks', project.id], queryFn: () => projectRisksApi.listForProject(project.id) });
  const { register, handleSubmit, reset, formState: { errors } } = useForm<z.infer<typeof riskSchema>>({ resolver: zodResolver(riskSchema) });

  const createMutation = useMutation({
    mutationFn: (values: z.infer<typeof riskSchema>) => projectRisksApi.create(project.id, values),
    onSuccess: () => { showSuccess('Risk added.'); queryClient.invalidateQueries({ queryKey: ['project-risks', project.id] }); setModalOpen(false); reset(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to add risk.'),
  });

  return (
    <>
      <div className="panel">
        <SectionHeader title="Risk Register" actions={canManage && <button className="btn-primary" onClick={() => setModalOpen(true)}>Add Risk</button>} />
        <div className="panel-body">
      <CommentThread projectId={project.id} entityType="risk" />

      {risksQuery.data && (
        <DataTable
          rows={risksQuery.data.data}
          rowKey={(r) => r.id}
          emptyMessage="No risks recorded yet."
          columns={[
            { header: 'Risk', render: (r) => r.risk_description },
            { header: 'Category', render: (r) => r.category },
            { header: 'Rating', render: (r) => <span style={{ color: RATING_COLORS[r.overall_rating], fontWeight: 700 }}>{r.overall_rating.toUpperCase()}</span> },
            { header: 'Status', render: (r) => <StatusBadge status={r.status} /> },
          ]}
        />
      )}
        </div>
      </div>

      {modalOpen && (
        <Modal title="Add Risk" onClose={() => setModalOpen(false)}>
          <p className="muted">Overall rating is calculated automatically from Likelihood x Impact.</p>
          <form onSubmit={handleSubmit((values) => createMutation.mutate(values))}>
            <label>Risk Description<textarea rows={2} {...register('riskDescription')} />{errors.riskDescription && <span className="field-error">{errors.riskDescription.message}</span>}</label>
            <label>
              Category
              <select {...register('category')}>
                {['security', 'access', 'programme', 'financial', 'operational', 'procurement', 'staffing', 'reputational', 'safeguarding', 'environmental', 'political', 'other'].map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
            <label>Likelihood<select {...register('likelihood')}><option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option></select></label>
            <label>Impact<select {...register('impact')}><option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option></select></label>
            <label>Mitigation<textarea rows={2} {...register('mitigation')} /></label>
            <div className="form-actions"><button type="submit" className="btn-primary" disabled={createMutation.isPending}>{createMutation.isPending ? 'Saving…' : 'Add'}</button><button type="button" className="btn-secondary" onClick={() => setModalOpen(false)}>Cancel</button></div>
          </form>
        </Modal>
      )}

      <section className="card">
        <h2>Safeguarding &amp; Cross-Cutting</h2>
        <p className="muted">Gender, Disability, Protection, PSEA, Accountability to Affected People, Safeguarding, and Conflict Sensitivity are captured as structured fields on the Project Design tab.</p>
      </section>
    </>
  );
}
